import { getDateLocale as getInterfaceDateLocale } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CaptureRecord, ReportsPagination } from './types';
import { ReportsRowActions } from './ReportsRowActions';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Mail, Phone } from 'lucide-react';

interface ReportsMobileListProps {
  records: CaptureRecord[];
  pagination: ReportsPagination;
  onPaginationChange: (pagination: ReportsPagination) => void;
  onExportRow: (record: CaptureRecord) => void;
  onDelete?: (record: CaptureRecord) => void;
}

export const ReportsMobileList: React.FC<ReportsMobileListProps> = ({
  records,
  pagination,
  onPaginationChange,
  onExportRow,
  onDelete,
}) => {
  useLanguage();
  const totalPages = Math.ceil(pagination.totalRecords / pagination.pageSize);
  const startRecord = (pagination.page - 1) * pagination.pageSize + 1;
  const endRecord = Math.min(pagination.page * pagination.pageSize, pagination.totalRecords);

  if (records.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        {translateInterfaceText("Nenhum registro encontrado")}</div>
    );
  }

  return (
    <div className="space-y-4">
      {records.map((record) => (
        <Card key={record.id} className="border border-[#E5E7EB] rounded-[14px] p-4 hover:border-[#2563EB] transition-all">
          {/* Header do card */}
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="font-semibold text-gray-900 mb-1">
                {record.name}
              </div>
              <div className="text-sm text-gray-500">
                {record.email || '-'}
              </div>
            </div>
            <ReportsRowActions
              record={record}
              onExportRow={onExportRow}
              onDelete={onDelete}
            />
          </div>

          {/* Divisor */}
          <div className="border-t border-gray-100 my-3" />

          {/* Detalhes */}
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">{translateInterfaceText("Telefone:")}</span>
              <span className="text-gray-900">{record.phone || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">{translateInterfaceText("Tipo:")}</span>
              <Badge
                variant="secondary"
                className={
                  record.type === 'data_capture'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-purple-100 text-purple-700'
                }
              >
                {record.type === 'data_capture' ? translateInterfaceText("Dados") : translateInterfaceText("Senha")}
              </Badge>
            </div>
            <div className="flex justify-between items-start gap-2">
              <span className="text-gray-500">{translateInterfaceText("Vídeo:")}</span>
              <span className="text-gray-900 text-right truncate max-w-[200px]">
                {record.video_name}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Data:</span>
              <span className="text-gray-900">
                {format(new Date(record.created_at_utc), 'dd/MM/yyyy HH:mm', {
                  locale: getInterfaceDateLocale(),
                })}
              </span>
            </div>
          </div>
        </Card>
      ))}

      {/* Paginação */}
      <div className="flex flex-col gap-4 pt-4">
        <div className="flex items-center justify-center gap-2">
          <span className="text-sm text-muted-foreground">{translateInterfaceText("Linhas por página:")}</span>
          <Select
            value={pagination.pageSize.toString()}
            onValueChange={(value) =>
              onPaginationChange({
                ...pagination,
                pageSize: parseInt(value) as 25 | 50 | 100,
                page: 1,
              })
            }
          >
            <SelectTrigger className="w-[70px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              onPaginationChange({ ...pagination, page: pagination.page - 1 })
            }
            disabled={pagination.page === 1}
          >
            {translateInterfaceText("Anterior")}</Button>
          <span className="text-sm text-muted-foreground">
            {startRecord}-{endRecord} {" "}{translateInterfaceText("de")}{" "}{pagination.totalRecords}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              onPaginationChange({ ...pagination, page: pagination.page + 1 })
            }
            disabled={pagination.page >= totalPages}
          >
            {translateInterfaceText("Próxima")}</Button>
        </div>
      </div>
    </div>
  );
};
